// Traductor de voz: capa de abstracción sobre el motor real de
// reconocimiento (STT) y síntesis (TTS) de voz.
//
// Hoy la única implementación es `webSpeechEngine`, que usa las Web Speech
// APIs del navegador — gratis, sin backend nuevo, sin llave nueva. Cuando el
// uso (y por ende la facturación) lo justifique, se agrega una segunda
// implementación de esta misma interfaz (por ejemplo, Google Cloud
// Speech-to-Text/Text-to-Speech) y se cambia únicamente `activeEngine` más
// abajo — nada que llame a `listen()`/`speak()`/`isVoiceSupported()` desde
// las pantallas necesita tocarse.

// Códigos BCP-47 para cada idioma que ya ofrece el traductor de texto —
// los usan tanto el reconocimiento como la síntesis de voz.
export const VOICE_LANG_CODES = {
  "Español": "es-MX",
  "English": "en-US",
  "Français": "fr-FR",
  "Português": "pt-BR",
  "Italiano": "it-IT",
  "Deutsch": "de-DE",
  "日本語": "ja-JP",
};

const webSpeechEngine = {
  isSupported() {
    return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  },

  // Escucha una sola frase y resuelve con el texto reconocido.
  // Devuelve un "handle" con `.stop()` por si el llamador quiere cancelar.
  listen({ lang, onResult, onError, onEnd }) {
    const SpeechRecognitionImpl = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionImpl();
    recognition.lang = lang;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (e) => onResult(e.results[0][0].transcript);
    recognition.onerror = (e) => onError?.(e.error);
    recognition.onend = () => onEnd?.();
    recognition.start();
    return { stop: () => recognition.abort() };
  },

  speak({ text, lang, onEnd, onError }) {
    if (!window.speechSynthesis) {
      onError?.("not-supported");
      return { stop: () => {} };
    }
    // Cancela cualquier lectura en curso — evita que dos traducciones
    // seguidas se encimen hablando al mismo tiempo.
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang;
    utterance.onend = () => onEnd?.();
    utterance.onerror = () => onError?.("speech-error");
    window.speechSynthesis.speak(utterance);
    return { stop: () => window.speechSynthesis.cancel() };
  },
};

const activeEngine = webSpeechEngine;

export function isVoiceSupported() {
  return activeEngine.isSupported();
}

export function listen(opts) {
  return activeEngine.listen(opts);
}

export function speak(opts) {
  return activeEngine.speak(opts);
}
